/**
 * ImageLibrary — admin uploads reusable images (email banners, marketing
 * assets, etc.) and gets a public direct URL to paste anywhere external.
 *
 * Route: /control-panel-settings/image-library
 * Wired from ControlPanelSettings.js.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Copy, Trash2, Edit3, Check, X, RefreshCw, Loader2,
  ImageIcon, ExternalLink, Search, ImagePlus,
} from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MB  = 5;
const ACCEPT  = 'image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml';

export default function ImageLibrary() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/image-library`, {
        headers: getAuthHeaders(),
      });
      setItems(r.data.data || []);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const removeItem = async (id) => {
    if (!window.confirm(t('حذف هذه الصورة؟ الرابط المباشر سيتوقف عن العمل.',
                          'Delete this image? The direct URL will stop working.'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/image-library/${id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const updateItem = async (id, patch) => {
    try {
      await axios.patch(`${API_URL}/api/admin/image-library/${id}`, patch, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ', 'Saved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    }
  };

  const filtered = items.filter(x => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (x.title || '').toLowerCase().includes(q)
        || (x.description || '').toLowerCase().includes(q)
        || (x.filename || '').toLowerCase().includes(q);
  });

  return (
    <DashboardLayout
      title="رفع الصور"
      titleEn="Image Library"
      subtitle="ارفع الصور واحصل على رابط مباشر لاستخدامها في الإيميلات وأي مكان خارجي"
      subtitleEn="Upload images and get direct URLs for use in emails and any external channel"
      screenCode="CPL-IMG"
      onBack={() => navigate('/control-panel-settings')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'رفع الصور', labelEn: 'Image Library' },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="image-library-page">

        {/* Uploader */}
        <UploadCard onDone={load} t={t} isAr={isAr} />

        {/* Search bar + refresh */}
        <div className="mt-6 mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder={t('ابحث بالعنوان أو الوصف…', 'Search by title or description…')}
                   className="bg-[#050505] border-white/10 text-white ps-9"
                   data-testid="image-library-search" />
          </div>
          <Button variant="outline" onClick={load}
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5">
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="p-16 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-white/40" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center bg-white/[0.02] border border-dashed border-white/10 rounded-2xl">
            <ImageIcon className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 text-sm">
              {items.length === 0
                ? t('لا صور بعد. ارفع أول صورة من الأعلى.', 'No images yet. Upload the first one above.')
                : t('لا نتائج مطابقة للبحث.', 'No results match your search.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(item => (
              <ImageCard key={item.id}
                         item={item}
                         onDelete={() => removeItem(item.id)}
                         onUpdate={(patch) => updateItem(item.id, patch)}
                         t={t} isAr={isAr} />
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}


// ── Upload card ────────────────────────────────────────────────
function UploadCard({ onDone, t, isAr }) {
  const [title, setTitle]         = useState('');
  const [description, setDesc]    = useState('');
  const [file, setFile]           = useState(null);
  const [previewUrl, setPreview]  = useState(null);
  const [saving, setSaving]       = useState(false);
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ACCEPT.split(',').includes(f.type) && !ACCEPT.includes(f.type)) {
      toast.error(t('نوع غير مدعوم. المدعوم: PNG/JPG/WebP/GIF/SVG', 'Unsupported type. Allowed: PNG/JPG/WebP/GIF/SVG'));
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(t(`حجم الصورة يتجاوز ${MAX_MB}MB`, `Image exceeds ${MAX_MB}MB`));
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setFile(null); setPreview(null); setTitle(''); setDesc('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const submit = async () => {
    if (!file) { toast.error(t('اختر صورة أولاً', 'Pick an image first')); return; }
    if (!title.trim()) { toast.error(t('اكتب عنوان الصورة', 'Enter an image title')); return; }
    setSaving(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const r = await axios.post(`${API_URL}/api/admin/image-library`, {
        title:       title.trim(),
        description: description.trim(),
        filename:    file.name,
        mime_type:   file.type,
        data_url:    dataUrl,
      }, { headers: getAuthHeaders() });
      toast.success(t('تم الرفع', 'Uploaded'));
      // Copy the direct URL for convenience
      const url = r.data?.public_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success(t('الرابط المباشر منسوخ للحافظة', 'Direct URL copied to clipboard'));
        } catch { /* clipboard denied */ }
      }
      reset();
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الرفع', 'Upload failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#d4af37]/5 to-transparent border border-[#d4af37]/20 rounded-2xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center">
          <ImagePlus className="w-6 h-6 text-[#d4af37]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">{t('رفع صورة جديدة', 'Upload a new image')}</h2>
          <p className="text-xs text-white/50">
            {t('الحد الأقصى ' + MAX_MB + 'MB. المدعوم PNG/JPG/WebP/GIF/SVG.',
               `Max ${MAX_MB}MB. Allowed: PNG/JPG/WebP/GIF/SVG.`)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Left: file picker + preview */}
        <div className="md:col-span-1">
          <label htmlFor="il-file-input"
                 className="block cursor-pointer group">
            <div className="border-2 border-dashed border-white/15 hover:border-[#d4af37]/60 rounded-xl h-40 flex items-center justify-center transition bg-black/20 overflow-hidden">
              {previewUrl ? (
                <img src={previewUrl} alt="preview" className="max-h-full max-w-full object-contain" />
              ) : (
                <div className="text-center text-white/50">
                  <Upload className="w-8 h-8 mx-auto mb-2 group-hover:text-[#d4af37] transition" />
                  <span className="text-sm">{t('اختر ملف صورة', 'Pick an image file')}</span>
                </div>
              )}
            </div>
          </label>
          <input id="il-file-input" ref={inputRef} type="file" hidden accept={ACCEPT} onChange={onPick} data-testid="image-library-file-input" />
          {file && (
            <div className="text-[11px] text-white/50 mt-1 truncate">
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </div>
          )}
        </div>

        {/* Right: title, description, submit */}
        <div className="md:col-span-2 flex flex-col gap-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">{t('عنوان الصورة *', 'Image title *')}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder={t('مثال: بانر النشرة البريدية الشهرية', 'e.g. Monthly newsletter banner')}
                   className="bg-[#050505] border-white/10 text-white"
                   data-testid="image-library-title" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">
              {t('لماذا سنستخدم هذه الصورة؟ (اختياري)', 'What is this image used for? (optional)')}
            </label>
            <textarea value={description} onChange={(e) => setDesc(e.target.value)}
                      rows={2}
                      placeholder={t('مثال: للاستخدام في رأس رسائل الإيميل للعملاء الجدد',
                                     'e.g. Used at the top of new-customer welcome emails')}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-2 text-sm resize-y"
                      data-testid="image-library-desc" />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Button onClick={submit} disabled={saving || !file}
                    className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold flex-1"
                    data-testid="image-library-upload-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Upload className="w-4 h-4 me-2" />}
              {t('رفع الصورة والحصول على الرابط', 'Upload & get direct URL')}
            </Button>
            {file && (
              <Button variant="outline" onClick={reset}
                      className="border-white/10 bg-transparent text-white/70 hover:bg-white/5">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Individual image card ─────────────────────────────────────
function ImageCard({ item, onDelete, onUpdate, t, isAr }) {
  const [editing, setEditing] = useState(false);
  const [title,   setTitle]   = useState(item.title || '');
  const [desc,    setDesc]    = useState(item.description || '');

  const directUrl = `${API_URL}/api/public/i/${item.id}.${item.extension || 'png'}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(directUrl);
      toast.success(t('تم نسخ الرابط', 'URL copied'));
    } catch {
      toast.error(t('فشل النسخ', 'Copy failed'));
    }
  };

  const saveEdit = async () => {
    if (!title.trim()) { toast.error(t('العنوان مطلوب', 'Title is required')); return; }
    await onUpdate({ title: title.trim(), description: desc.trim() });
    setEditing(false);
  };

  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
         data-testid={`image-card-${item.id}`}>
      {/* Preview */}
      <a href={directUrl} target="_blank" rel="noopener noreferrer"
         className="block h-40 bg-black/40 flex items-center justify-center overflow-hidden group">
        <img src={directUrl} alt={item.title}
             className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
             loading="lazy" />
      </a>

      <div className="p-4 flex-1 flex flex-col gap-3">
        {editing ? (
          <>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder={t('عنوان', 'Title')}
                   className="bg-[#050505] border-white/10 text-white h-9 text-sm"
                   data-testid={`image-card-title-input-${item.id}`} />
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)}
                      rows={2}
                      placeholder={t('الوصف', 'Description')}
                      className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-2 text-xs resize-y"
                      data-testid={`image-card-desc-input-${item.id}`} />
            <div className="flex items-center gap-2">
              <Button onClick={saveEdit} size="sm"
                      className="bg-[#d4af37] text-black hover:bg-[#d4af37]/90 flex-1"
                      data-testid={`image-card-save-${item.id}`}>
                <Check className="w-3.5 h-3.5 me-1" />{t('حفظ', 'Save')}
              </Button>
              <Button variant="outline" size="sm"
                      onClick={() => { setEditing(false); setTitle(item.title || ''); setDesc(item.description || ''); }}
                      className="border-white/10 bg-transparent text-white/70">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <h3 className="text-white font-semibold text-sm">{item.title || t('بدون عنوان', 'Untitled')}</h3>
              {item.description && (
                <p className="text-white/50 text-xs mt-1 line-clamp-2">{item.description}</p>
              )}
            </div>

            {/* Direct URL box */}
            <div className="bg-[#050505] border border-white/10 rounded-lg p-2 flex items-center gap-2">
              <code dir="ltr"
                    className="text-[10px] text-white/60 font-mono truncate flex-1"
                    title={directUrl}>
                {directUrl}
              </code>
              <button onClick={copy}
                      className="p-1.5 rounded hover:bg-[#d4af37]/10 text-[#d4af37]"
                      title={t('نسخ الرابط', 'Copy URL')}
                      data-testid={`image-card-copy-${item.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a href={directUrl} target="_blank" rel="noopener noreferrer"
                 className="p-1.5 rounded hover:bg-white/10 text-white/60"
                 title={t('فتح', 'Open')}>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Meta */}
            <div className="text-[10px] text-white/40 flex items-center gap-3">
              <span>{(item.size / 1024).toFixed(0)} KB</span>
              <span>·</span>
              <span>{(item.mime_type || '').replace('image/', '').toUpperCase()}</span>
              {item.uses_count > 0 && (
                <>
                  <span>·</span>
                  <span>{t('عدد الاستخدامات', 'Views')}: {item.uses_count}</span>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 mt-auto pt-1">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}
                      className="border-white/10 bg-transparent text-white/70 hover:bg-white/5 flex-1"
                      data-testid={`image-card-edit-${item.id}`}>
                <Edit3 className="w-3.5 h-3.5 me-1" />{t('تعديل', 'Edit')}
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete}
                      className="border-red-500/20 bg-transparent text-red-400 hover:bg-red-500/10"
                      data-testid={`image-card-delete-${item.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
